import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { LanguageProvider } from "./contexts/LanguageContext";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { WhatsAppButton } from "./components/WhatsAppButton";
import { DisclaimerModal } from "./components/DisclaimerModal";
import HomePage from "./pages/HomePage";
import CatalogPage from "./pages/CatalogPage";
import ProductPage from "./pages/ProductPage";
import COAPage from "./pages/COAPage";
import ContactPage from "./pages/ContactPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import USBlockedPage from "./pages/USBlockedPage";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminPeptides from "./pages/admin/AdminPeptides";
import AdminCOAs from "./pages/admin/AdminCOAs";

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }
  
  return children;
};

// US Geo-blocking wrapper
const GeoBlockedRoute = ({ children }) => {
  const [isUS, setIsUS] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkGeo = async () => {
      // Check if already verified in session
      const verified = sessionStorage.getItem('geo_verified');
      if (verified === 'non-us') {
        setIsUS(false);
        setChecking(false);
        return;
      }
      if (verified === 'us') {
        setIsUS(true);
        setChecking(false);
        return;
      }

      try {
        // Use a free geo IP service
        const response = await fetch('https://ipapi.co/json/', { 
          timeout: 3000,
          headers: { 'Accept': 'application/json' }
        });
        
        if (response.ok) {
          const data = await response.json();
          const countryCode = data.country_code || data.country;
          
          if (countryCode === 'US') {
            setIsUS(true);
            sessionStorage.setItem('geo_verified', 'us');
          } else {
            setIsUS(false);
            sessionStorage.setItem('geo_verified', 'non-us');
          }
        } else {
          // If service fails, allow access
          setIsUS(false);
          sessionStorage.setItem('geo_verified', 'non-us');
        }
      } catch (error) {
        console.log('Geo check failed, allowing access');
        setIsUS(false);
        sessionStorage.setItem('geo_verified', 'non-us');
      }
      
      setChecking(false);
    };

    checkGeo();
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-neutral-950 flex items-center justify-center">
        <div className="animate-pulse text-neutral-400">Loading...</div>
      </div>
    );
  }

  if (isUS) {
    return <USBlockedPage />;
  }

  return children;
};

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <div className="App">
          <BrowserRouter>
            <Routes>
              {/* Admin Routes - No navbar/footer */}
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={
                <ProtectedRoute><AdminDashboard /></ProtectedRoute>
              } />
              <Route path="/admin/settings" element={
                <ProtectedRoute><AdminSettings /></ProtectedRoute>
              } />
              <Route path="/admin/peptides" element={
                <ProtectedRoute><AdminPeptides /></ProtectedRoute>
              } />
              <Route path="/admin/coas" element={
                <ProtectedRoute><AdminCOAs /></ProtectedRoute>
              } />
              
              {/* Public Routes - With US geo-blocking */}
              <Route path="/*" element={
                <GeoBlockedRoute>
                  <>
                    <DisclaimerModal />
                    <Navbar />
                    <main>
                      <Routes>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/catalog" element={<CatalogPage />} />
                        <Route path="/peptide/:catNo" element={<ProductPage />} />
                        <Route path="/coa" element={<COAPage />} />
                        <Route path="/contact" element={<ContactPage />} />
                        <Route path="/terms" element={<TermsPage />} />
                        <Route path="/privacy" element={<PrivacyPage />} />
                      </Routes>
                    </main>
                    <Footer />
                    <WhatsAppButton />
                  </>
                </GeoBlockedRoute>
              } />
            </Routes>
          </BrowserRouter>
        </div>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
